import type { Ref } from 'vue';
import { computed } from 'vue';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import { storeToRefs } from 'pinia';
import { Feature, useFeatureStore } from '@getaccept/feature-restriction';
import type { PricingTableColumn, PricingTableSection, ColumnType } from '../types/pricing-table';
import { PricingTableHelper } from '../helpers/pricing-table.helper';
import { Align, NumberInputType, EPricingTableColumn } from '../types/enums';
import { maxCustomColumns, minColumnWidth, nonGridColumns } from '../constants/pricing-table';
import { useEditorMetaDataStore } from '../store/editor-meta-data.store';
import type { EditorProductProperty } from '../types/editor-product-property';

export function usePricingTableColumns(
  section: Ref<PricingTableSection>,
  preCalculated: Ref<boolean>,
  productProperties: Ref<EditorProductProperty[]>
) {
  const { features } = storeToRefs(useFeatureStore());

  const metaDataStore = useEditorMetaDataStore();

  const columns = computed(() => section.value?.columns || []);
  const enabledColumns = computed(() => PricingTableHelper.getEnabledColumns(columns.value));
  const columnWidths = computed(() => enabledColumns.value?.map(({ width }) => width));

  const customColumns = computed(() => columns.value.filter(({ isCustom }) => isCustom));
  const customColumnThresholdReached = computed(() => {
    const maxColumns = !features.value[Feature.CustomColumns].restricted ? maxCustomColumns : 1;
    return customColumns.value.length >= maxColumns;
  });

  const maxEnabledColumnsCount = Math.floor(100 / minColumnWidth);

  const vacantColumnCount = computed(() =>
    Math.max(0, maxEnabledColumnsCount - enabledColumns.value.length)
  );

  const maxEnabledColumnsReached = computed(
    () => enabledColumns.value.length >= maxEnabledColumnsCount
  );

  const getColumnById = (columnId: string) => columns.value.find(({ id }) => id === columnId);

  const getColumnByName = (columnName: EPricingTableColumn) =>
    columns.value.find(({ name }) => name === columnName);

  const isColumnEnabled = (column: PricingTableColumn) => column?.enabled || false;
  const isGridColumn = (column: PricingTableColumn) =>
    !nonGridColumns.includes(column.name as EPricingTableColumn) || !!column.productPropertyId;
  const isProductPropertyColumn = (column: PricingTableColumn) => !!column.productPropertyId;

  const enableColumn = (column: PricingTableColumn, updatedSection?: PricingTableSection) => {
    if (isProductPropertyColumn(column)) {
      metaDataStore.productPropertyChangedEvent.trigger();
    }

    return preCalculated.value
      ? PricingTableHelper.enableColumn(column.id, updatedSection || section.value)
      : PricingTableHelper.enableColumnAndReCalculate(column.id, updatedSection || section.value);
  };

  const disableColumn = (column: PricingTableColumn) => {
    if (isProductPropertyColumn(column)) {
      metaDataStore.productPropertyChangedEvent.trigger();
    }

    let updatedSection = preCalculated.value
      ? PricingTableHelper.disableColumn(column.id, section.value)
      : PricingTableHelper.disableColumnAndReCalculate(column.id, section.value);

    if (column.name === EPricingTableColumn.TotalPrice) {
      const discountColumn = getColumnByName(EPricingTableColumn.Discount);
      updatedSection = preCalculated.value
        ? PricingTableHelper.disableColumn(discountColumn.id, updatedSection)
        : PricingTableHelper.disableColumnAndReCalculate(discountColumn.id, updatedSection);
    }

    return updatedSection;
  };

  const discountColumn = computed(() => getColumnByName(EPricingTableColumn.Discount));
  const totalPriceColumn = computed(() => getColumnByName(EPricingTableColumn.TotalPrice));
  const taxColumn = computed(() => getColumnByName(EPricingTableColumn.Tax));

  const getColumnByProductPropertyId = (productPropertyId: string) =>
    columns.value.find(column => column.productPropertyId === productPropertyId);

  const getProductPropertyById = (productPropertyId: string) =>
    (productProperties.value || []).find(({ id }) => id === productPropertyId);

  const isProductPropertyEnabled = (productPropertyId: string) =>
    isColumnEnabled(getColumnByProductPropertyId(productPropertyId));

  const shouldReferenceProductProperty = (column: PricingTableColumn) =>
    isProductPropertyColumn(column) && metaDataStore.isProductPropertyReferenceContext;

  const isCustomColumn = (column: PricingTableColumn) => column.isCustom;

  const isPredefinedColumn = (column: PricingTableColumn) => !isProductPropertyColumn(column);

  const createColumnFromProductProperty = (
    productPropertyId: string,
    columnProps?: Partial<PricingTableColumn>
  ) => {
    const productProperty = getProductPropertyById(productPropertyId);
    return metaDataStore.isProductPropertyReferenceContext
      ? PricingTableHelper.createColumn({
          productPropertyId,
          type: productProperty.type,
          unit: productProperty.type === 'Number' ? productProperty.unit : null,
          ...(columnProps ?? {}),
        })
      : PricingTableHelper.createColumnFromProductProperty(productProperty, columnProps);
  };

  const insertColumn = (
    newColumn: PricingTableColumn,
    section: PricingTableSection
  ): PricingTableSection => {
    const updatedColumns: PricingTableColumn[] = [
      ...section.columns.slice(0, -1),
      newColumn,
      section.columns[section.columns.length - 1],
    ];

    const updatedRows = section.rows.map(row => ({
      ...row,
      values: [...row.values, { columnId: newColumn.id, value: '' }],
    }));

    return {
      ...section,
      rows: updatedRows,
      columns: updatedColumns,
    };
  };

  const enableProductProperties = (productPropertyIds: string[]) => {
    let updatedSection = { ...section.value };

    productPropertyIds.forEach(productPropertyId => {
      const existingColumn = getColumnByProductPropertyId(productPropertyId);

      if (existingColumn) {
        updatedSection = enableColumn(existingColumn, updatedSection);
        return;
      }

      const column = createColumnFromProductProperty(productPropertyId);
      updatedSection = insertColumn(column, updatedSection);
      updatedSection = enableColumn(column, updatedSection);
    });

    return updatedSection;
  };

  const disableProductProperties = (productPropertyIds: string[]) => {
    let updatedSection = { ...section.value };

    productPropertyIds.forEach(productPropertyId => {
      updatedSection = disableColumn(getColumnByProductPropertyId(productPropertyId));
    });

    return updatedSection;
  };

  const getColumnDisplayName = (column: PricingTableColumn) => {
    if (shouldReferenceProductProperty(column)) {
      return (
        column.displayName || getProductPropertyById(column.productPropertyId)?.displayName || ''
      );
    }

    if (column.displayName) {
      return column.displayName;
    }

    if (isPredefinedColumn(column)) {
      const translationKey =
        column.name === EPricingTableColumn.TotalPrice ? 'total-price' : column.name;

      return translationKey ? t(translationKey) : '';
    }

    return '';
  };

  const getColumnName = (column: PricingTableColumn) => {
    if (shouldReferenceProductProperty(column)) {
      return getProductPropertyById(column.productPropertyId)?.name || '';
    }

    return column.name;
  };

  const getColumnType = (column: PricingTableColumn): ColumnType => {
    if (column.type) {
      return column.type;
    }

    if (column.isCustom) {
      return 'Text';
    }

    switch (column.name) {
      case EPricingTableColumn.Name:
      case EPricingTableColumn.Description:
      case EPricingTableColumn.Sku:
        return 'Text';
      case EPricingTableColumn.Price:
      case EPricingTableColumn.Units:
      case EPricingTableColumn.Tax:
      case EPricingTableColumn.TotalPrice:
      case EPricingTableColumn.NetPrice:
      case EPricingTableColumn.Discount:
        return 'Number';
    }

    return 'Text';
  };

  const hasProductPropertyReferences = computed(() =>
    columns.value.some(
      column => metaDataStore.isProductPropertyReferenceContext && isProductPropertyColumn(column)
    )
  );

  const getColumnNumberInputType = (columnId: string): NumberInputType => {
    const column = getColumnById(columnId);

    if (!column) {
      return NumberInputType.Number;
    }

    switch (column?.unit) {
      case 'Currency':
        return NumberInputType.Currency;
      case 'Percentage':
        return NumberInputType.Percentage;
      case 'Unknown':
        return NumberInputType.Number;
      default:
    }

    switch (column?.name) {
      case EPricingTableColumn.Price:
      case EPricingTableColumn.TotalPrice:
        return NumberInputType.Currency;
      case EPricingTableColumn.Tax:
        return NumberInputType.Percentage;
      default:
        return NumberInputType.Number;
    }
  };

  const getColumnAlignment = (column: PricingTableColumn): Align =>
    getColumnType(column) === 'Number' ? Align.Right : Align.Left;

  return {
    columns,
    enabledColumns,
    columnWidths,
    customColumns,
    discountColumn,
    totalPriceColumn,
    taxColumn,
    customColumnThresholdReached,
    vacantColumnCount,
    maxEnabledColumnsReached,
    getColumnDisplayName,
    getColumnName,
    getColumnByName,
    getColumnById,
    isColumnEnabled,
    isGridColumn,
    enableColumn,
    disableColumn,
    isProductPropertyEnabled,
    enableProductProperties,
    disableProductProperties,
    getColumnByProductPropertyId,
    createColumnFromProductProperty,
    isProductPropertyColumn,
    isCustomColumn,
    getProductPropertyById,
    getColumnNumberInputType,
    hasProductPropertyReferences,
    getColumnAlignment,
    getColumnType,
  };
}
